import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import {
  formatArticleDate,
  getVisitCounts,
  listPublishedArticles,
  readingMinutes,
  recordBlogVisit,
  tagList,
  type BlogArticle,
  type VisitCounts,
} from '@/lib/blog';

const FALLBACK_COVER =
  'https://readdy.ai/api/search-image?query=Minimal%20editorial%20workspace%20flatlay%20with%20notebook%20and%20warm%20neutral%20tones%2C%20soft%20daylight%2C%20clean%20modern%20composition%2C%20high%20detail&width=800&height=500&seq=blog-fallback-cover-2026&orientation=landscape';

export default function BlogPage() {
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [counts, setCounts] = useState<VisitCounts>({ total: 0, byArticle: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tag, setTag] = useState<string>('all');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [list, visits] = await Promise.all([listPublishedArticles(), getVisitCounts()]);
        if (!alive) return;
        setArticles(list);
        setCounts(visits);
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : 'Impossible de charger le blog.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    void recordBlogVisit(null, '/blog');
    return () => {
      alive = false;
    };
  }, []);

  const tags = useMemo(() => {
    const set = new Set<string>();
    articles.forEach((a) => tagList(a.tags).forEach((t) => set.add(t)));
    return Array.from(set).slice(0, 10);
  }, [articles]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return articles.filter((a) => {
      if (tag !== 'all' && !tagList(a.tags).includes(tag)) return false;
      if (!q) return true;
      return `${a.title} ${a.excerpt || ''} ${a.tags || ''}`.toLowerCase().includes(q);
    });
  }, [articles, search, tag]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar forceScrolled />

      <header className="pt-24 md:pt-32 pb-10 md:pb-14 px-4 md:px-6 lg:px-10 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-semibold">
            <i className="ri-article-line"></i>Blog Zifek
          </span>
          <h1 className="mt-4 text-3xl md:text-5xl font-bold font-heading text-foreground-950 leading-tight max-w-3xl">
            Idées, guides et nouveautés pour faire grandir votre commerce
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl">
            Tout ce qu’il faut savoir sur la gestion, la facturation, la vente en ligne et la conformité
            au Maroc — écrit simplement, sans jargon.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3 md:gap-6">
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <i className="ri-file-text-line text-primary-500"></i>
              <strong className="text-foreground-950">{articles.length}</strong> articles publiés
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <i className="ri-eye-line text-accent-600"></i>
              <strong className="text-foreground-950">{counts.total.toLocaleString('fr-FR')}</strong> lectures
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un article…"
                className="w-full pl-9 pr-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
            </div>
            <select
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              className="px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-700 cursor-pointer focus:outline-none"
            >
              <option value="all">Toutes les catégories</option>
              {tags.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <main className="px-4 md:px-6 lg:px-10 py-10 md:py-14">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
            </div>
          ) : error ? (
            <div className="py-16 text-center">
              <i className="ri-error-warning-line text-3xl text-accent-600"></i>
              <p className="mt-3 text-sm text-foreground-600">{error}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center bg-background-50 border border-background-200/70 rounded-lg">
              <i className="ri-article-line text-3xl text-foreground-300"></i>
              <p className="mt-3 text-sm text-foreground-600">Aucun article ne correspond à votre recherche.</p>
            </div>
          ) : (
            <>
              {featured && (
                <Link
                  to={`/blog/${featured.slug || featured.id}`}
                  className="group grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-stretch bg-background-100 rounded-lg overflow-hidden no-underline mb-10"
                >
                  <div className="w-full h-56 lg:h-full min-h-[220px] overflow-hidden">
                    <img
                      src={featured.cover_image || FALLBACK_COVER}
                      alt={featured.title}
                      title={`${featured.title} — blog Zifek`}
                      className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 md:p-8 flex flex-col justify-center">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-accent-700">
                      À la une
                    </span>
                    <h2 className="mt-2 text-xl md:text-3xl font-bold font-heading text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors">
                      {featured.title}
                    </h2>
                    {featured.excerpt && (
                      <p className="mt-3 text-sm text-foreground-600 leading-relaxed line-clamp-3">{featured.excerpt}</p>
                    )}
                    <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {formatArticleDate(featured.published_at || featured.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {readingMinutes(featured.content)} min de lecture
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line text-accent-600"></i>
                        {(counts.byArticle[featured.id] || 0).toLocaleString('fr-FR')} vues
                      </span>
                    </div>
                  </div>
                </Link>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
                {rest.map((a) => (
                  <Link
                    key={a.id}
                    to={`/blog/${a.slug || a.id}`}
                    className="group flex flex-col bg-background-50 border border-background-200/70 rounded-lg overflow-hidden no-underline hover:border-primary-200 transition-colors"
                  >
                    <div className="w-full h-40 overflow-hidden bg-background-100">
                      <img
                        src={a.cover_image || FALLBACK_COVER}
                        alt={a.title}
                        title={`${a.title} — blog Zifek`}
                        className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 md:p-5 flex-1 flex flex-col">
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {tagList(a.tags).slice(0, 2).map((t) => (
                          <span key={t} className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[10px] font-semibold">
                            {t}
                          </span>
                        ))}
                      </div>
                      <h3 className="text-base font-semibold text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors">
                        {a.title}
                      </h3>
                      {a.excerpt && <p className="mt-2 text-xs text-foreground-600 line-clamp-3 flex-1">{a.excerpt}</p>}
                      <div className="mt-4 pt-3 border-t border-background-200/70 flex items-center justify-between text-[11px] text-foreground-500">
                        <span>{formatArticleDate(a.published_at || a.created_at)}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {(counts.byArticle[a.id] || 0).toLocaleString('fr-FR')}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}